"use client";

/**
 * LiquidPill
 * ──────────
 * Composite del design language v2. NO duplica CSS del surface liquido —
 * lo COMPONE delegando 100% de la superficie visual (gradientes, sombras,
 * filter, hover, squish, warp, refraction, transitions, dark mode) al
 * primitivo `LiquidSurface`.
 *
 * Este archivo solo aporta la especializacion semantica del "pill":
 *   · Padding y font-size escalonados (PILL_SIZES xs/sm/md/lg).
 *   · Dimensiones cuadradas y `p-0` cuando shape="circle" (CIRCLE_SIZES).
 *   · Layout interno inline-flex centrado.
 *   · Renderizado por defecto de icon + text si no se pasan children.
 *   · API de props `appearance="tinted"` / `active=true` mapeada a tone.
 *
 * Modificar el LiquidSurface (`liquid-surface.tsx`) propaga el cambio
 * automaticamente a TODOS los composites que se construyen sobre el pill:
 * LiquidPillDropdown, LiquidPillExpandButton, LiquidPillGroup,
 * LiquidPillGroupVertical, etc.
 *
 *   LiquidSurface  ←─ unico owner del CSS liquido
 *     ↑
 *   LiquidPill     ←─ shape + size + icon/text layout
 *     ↑
 *   LiquidPillDropdown / LiquidPillExpandButton / LiquidPillGroup
 */

import * as React from "react";
import { cn } from "@/lib/utils";
import { type CircleActionVariant } from "./circle-action-variants";
import {
  LiquidSurface,
  type LiquidDeformAxis,
  type LiquidSurfaceShadow,
  type LiquidSurfaceShape,
  type LiquidSurfaceStyle,
  type LiquidSurfaceBounceIntensity,
} from "./liquid-surface";

export type LiquidPillAppearance = "normal" | "tinted";
export type LiquidPillShape = "pill" | "rounded" | "circle";
export type LiquidPillSize = "xxs" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "card-cta";

export interface LiquidPillProps {
  variant?: CircleActionVariant;
  /**
   * "normal" = surface translucido (tone neutral del LiquidSurface).
   * "tinted" = surface con tint de variante (tone tinted del LiquidSurface).
   * Se combina con `active`: cualquiera de los dos true → tinted.
   */
  appearance?: LiquidPillAppearance;
  shape?: LiquidPillShape;
  size?: LiquidPillSize;
  icon?: React.ReactNode;
  text?: React.ReactNode;
  children?: React.ReactNode;
  as?: "div" | "button" | "span";
  type?: "button" | "submit" | "reset";
  onClick?: React.MouseEventHandler<HTMLElement>;
  disabled?: boolean;
  /** Surface en estado tinted permanente (sin esperar hover). */
  active?: boolean;
  /** Variante visual compuesta del LiquidSurface interno. */
  surfaceStyle?: LiquidSurfaceStyle;
  /**
   * Activa hover/focus liquid (squish + warp + refraction + cross-fade).
   * Auto-true si `as="button"` o si hay `onClick`. La explicita en false
   * NO desactiva si as="button" o hay onClick (preservamos semantica
   * historica del LiquidPill original).
   */
  interactive?: boolean;
  /** Controla la elevacion/sombra del LiquidSurface interno. */
  shadow?: LiquidSurfaceShadow;
  /** Muestra la partícula especular del LiquidSurface interno. */
  cursorGlow?: boolean;
  /** Fuerza visible la partícula aunque la pill no reciba hover directo. */
  cursorGlowVisible?: boolean;
  /** Activa el material reactivo al fondo (background/backdrop + borde). */
  reactive?: boolean;
  /** Rebote líquido en hover, como el feedback del dropdown. */
  hoverBounce?: boolean;
  /** Deformación local del material según posición del puntero. */
  hoverDeform?: boolean;
  /** Intensidad del rebote/deformación líquida (LiquidSurface). */
  bounceIntensity?: LiquidSurfaceBounceIntensity;
  /** Eje de deformación al hover. Default `both`. */
  deformAxis?: LiquidDeformAxis;
  "aria-label"?: string;
  className?: string;
  style?: React.CSSProperties;
  form?: string;
  name?: string;
  value?: string;
  accessKey?: string;
  /** @deprecated rsuite Tag compat — ignored */
  color?: string;
  id?: string;
}

// Padding/gap/font escalonados para shape "pill" y "rounded".
const PILL_SIZES: Record<LiquidPillSize, string> = {
  xxs: "gap-1 px-1.5 py-[2px] text-[9px] leading-none font-medium",
  xs: "gap-1 px-2 py-[3px] text-[10px] leading-none font-medium",
  sm: "gap-1.5 px-3 py-[5px] text-[11px] leading-none font-medium",
  md: "gap-2 px-3.5 py-[7px] text-xs font-medium",
  lg: "gap-2 px-4 py-[9px] text-sm font-medium",
  xl: "gap-2.5 px-5 py-[11px] text-sm font-medium",
  "2xl": "gap-3 px-6 py-3 text-base font-medium",
  // CTA para footer de cards: 36px altura, ancho completo, centrado, texto §6.12
  "card-cta": "h-9 w-full justify-center gap-1.5 px-2 py-0 text-[11px] font-semibold text-slate-900 dark:text-white",
};

// Dimensiones cuadradas para shape "circle". `p-0` anula el padding del pill.
const CIRCLE_SIZES: Record<Exclude<LiquidPillSize, "card-cta">, string> = {
  xxs: "h-5 w-5 p-0",
  xs: "h-6 w-6 p-0",
  sm: "h-7 w-7 p-0",
  md: "h-9 w-9 p-0",
  lg: "h-11 w-11 p-0",
  xl: "h-14 w-14 p-0",
  "2xl": "h-16 w-16 p-0",
};

/** Diámetro en px de cada tamaño circle (para layout/stacking de FABs). */
export const LIQUID_PILL_CIRCLE_DIAMETER: Record<LiquidPillSize, number> = {
  xxs: 20,
  xs: 24,
  sm: 28,
  md: 36,
  lg: 44,
  xl: 56,
  "2xl": 64,
  // card-cta no es valido para circles, pero incluimos por completitud de tipo
  "card-cta": 36,
};

export const LiquidPill = React.forwardRef<
  HTMLButtonElement | HTMLDivElement | HTMLSpanElement,
  LiquidPillProps
>(function LiquidPill(
  {
    variant,
    appearance = "normal",
    shape = "pill",
    size = "sm",
    icon,
    text,
    children,
    as = "div",
    type = "button",
    onClick,
    disabled,
    active = false,
    surfaceStyle = "sidebarInactive",
    interactive = false,
    shadow = "default",
    cursorGlow = true,
    cursorGlowVisible = false,
    reactive = false,
    hoverBounce = false,
    hoverDeform = true,
    bounceIntensity,
    deformAxis = "both",
    className,
    style,
    form,
    name,
    value,
    id,
    accessKey,
    "aria-label": ariaLabel,
  },
  ref,
) {
  const isCircle = shape === "circle";
  const isSidebarIdleSurface = surfaceStyle === "sidebarInactive";

  // Tone: surface tinted si appearance="tinted" o active=true.
  const tone: "neutral" | "tinted" =
    appearance === "tinted" || active ? "tinted" : "neutral";

  // Interactive: explicito, o automatico por as="button" / onClick.
  // OR semantics (mantenemos el comportamiento original del LiquidPill).
  const isInteractive = interactive || as === "button" || Boolean(onClick);

  const resolvedShadow =
    shadow === "default" && isSidebarIdleSurface ? "neutral" : shadow;
  /* sidebarInactive apaga glow en idle; con hoverDeform sí mostramos partícula al mover. */
  const resolvedCursorGlow =
    isSidebarIdleSurface && !hoverDeform ? false : cursorGlow;

  // Layout y sizing especifico del pill.
  const sizeClass = isCircle ? CIRCLE_SIZES[size] : PILL_SIZES[size];

  // Inner por defecto si el consumer no pasa children. Replica el patron
  // original: icon (opcional) + text (oculto en shape circle).
  const inner =
    children ??
    (icon != null || text != null ? (
      <span className="flex items-center" style={{ gap: "inherit" }}>
        {icon != null && (
          <span className="flex shrink-0 items-center leading-none">{icon}</span>
        )}
        {text != null && !isCircle && (
          <span className="whitespace-nowrap">{text}</span>
        )}
      </span>
    ) : null);

  return (
    <LiquidSurface
      ref={ref as React.Ref<HTMLElement>}
      variant={variant}
      tone={tone}
      shape={shape as LiquidSurfaceShape}
      as={as}
      interactive={isInteractive}
      shadow={resolvedShadow}
      surfaceStyle={surfaceStyle}
      cursorGlow={resolvedCursorGlow}
      cursorGlowVisible={cursorGlowVisible}
      reactive={reactive}
      hoverBounce={hoverBounce}
      hoverDeform={hoverDeform}
      edgeStyle={isSidebarIdleSurface ? "flat" : undefined}
      bodyStyle={isSidebarIdleSurface ? "flat" : undefined}
      bounceIntensity={bounceIntensity}
      deformAxis={deformAxis}
      onClick={onClick}
      type={type}
      disabled={disabled}
      form={form}
      name={name}
      value={value}
      id={id}
      accessKey={accessKey}
      aria-label={ariaLabel}
      className={cn(
        // Marker class: util si algun consumer externo selecciona pills
        // especificamente con `.liquid-pill { ... }` desde un stylesheet.
        // No tiene reglas propias asociadas — todo el visual viene del
        // LiquidSurface.
        "liquid-pill",
        // Layout interno: inline-flex centrado, comportamiento de pill.
        "inline-flex shrink-0 items-center justify-center",
        "select-none tracking-[-0.01em]",
        // Sizing (padding/gap/font) o dimensiones cuadradas si circle.
        sizeClass,
        // El feedback de click lo maneja LiquidSurface. No usar active:scale
        // aquí porque se superpone con el pop/ripple y genera doble rebote.
        className,
      )}
      style={style}
    >
      {inner}
    </LiquidSurface>
  );
});

LiquidPill.displayName = "LiquidPill";
